import Button from 'devextreme-react/button';
import DataGrid, { Column, Editing, Format, Item, Selection, Toolbar } from 'devextreme-react/data-grid';
import NumberBox from 'devextreme-react/number-box';
import { formatNumber } from 'devextreme/localization';
import { useContext, useState } from 'react';
import { useAuth } from '../../../../contexts/auth';
import { CompanyTypes, OrderStatus, ProductLine } from '../../../../types/enums';
import { POInvoiceContext } from '../context/POInvoiceContext';
import { PurchaseOrderContext } from '../context/PurchaseOrderContext';
import { POInvoiceProduct } from '../types/POInvoiceProduct';
import NewInvoiceProductPopup from './NewInvoiceProductPopup';

function getSubtotal(products: POInvoiceProduct[]) {
    return products.filter(x => x.selected).reduce((a, b) => a + (b.unitPrice * (b.quantity ?? 0)), 0);
}

function getFreightAmount(products: POInvoiceProduct[]) {
    return products.filter(x => x.selected).reduce((a, b) => a + (b.freightCost ?? 0), 0);
}

function getTotal(subTotal: number, tax: number, discount: number, cutCharge: number, freight: number) {
    return subTotal + tax - discount + cutCharge + freight;
}

export default function PurchaseOrderNewInvoiceProducts() {

    const { order } = useContext(PurchaseOrderContext);
    const { invoice, modify } = useContext(POInvoiceContext);

    const [isPopupVisible, setPopupVisibility] = useState(false);

    const isLaminate = order.details.products.some((p) => {
        return p.productLineID === ProductLine.LaminateInstall || p.productLineID === ProductLine.LaminateDelivered;
    });

    let products = invoice.details.products?.map(x => x) ?? [];

    const { user } = useAuth();
    const isServiceProvider = user?.companyTypeId === CompanyTypes.SERVICE_PROVIDER;

    return (
        <>
            <NewInvoiceProductPopup
                visible={isPopupVisible}
                onPopupHidden={(product) => {
                    if (product) {
                        products.push(product);
                        modify({ ...invoice.details, products: products });
                    }
                    setPopupVisibility(false);
                }}
            />

            <DataGrid
                dataSource={products}
                allowColumnReordering={true}
                allowColumnResizing={true}
                showBorders={true}
                showColumnLines={true}
                wordWrapEnabled={true}
                keyExpr={'poInvProductID'}
                className="grid-with-header"
                onSelectionChanged={(e) => {
                    products = invoice.details.products.map(x => { return { ...x, selected: e.selectedRowKeys.includes(x.poInvProductID) } });
                    const subTotal = getSubtotal(products);
                    const freightAmount = getFreightAmount(products);
                    modify({
                        ...invoice.details,
                        subTotal: subTotal,
                        invoiceTotal: getTotal(subTotal, invoice.details.tax, invoice.details.discount,
                            invoice.details.cutCharge, freightAmount),
                        freightAmount: freightAmount,
                        products: products
                    });
                }}
                onRowUpdated={() => {
                    const subTotal = getSubtotal(products);
                    modify({
                        ...invoice.details,
                        subTotal: subTotal,
                        invoiceTotal: getTotal(subTotal, invoice.details.tax, invoice.details.discount,
                            invoice.details.cutCharge, invoice.details.freightAmount),
                        products: products
                    });
                }}
            >
                <Selection mode="multiple" allowSelectAll={true} />
                <Editing
                    mode="cell"
                    allowAdding={false}
                    allowDeleting={(e) => { return !e.row.data.poProductID }}
                    allowUpdating={order.details.orderStatusID === OrderStatus.Open}
                />

                <Toolbar>
                    <Item
                        visible={true}
                        location={'after'}
                    >
                        <Button elementAttr={{ 'data-testid': 'btn-add-line' }}
                            text="Add Line"
                            icon="Inscyth-Add"
                            disabled={order.details.orderStatusID === OrderStatus.Pending}
                            onClick={() => {
                                setPopupVisibility(true);
                            }}>
                        </Button>
                    </Item>
                </Toolbar>

                <Column
                    dataField={'sku'}
                    caption={'SKU'}
                    dataType="string"
                    allowEditing={false}
                />
                <Column
                    dataField={'itemDescription'}
                    caption={'Item Description'}
                    dataType="string"
                    encodeHtml={false}
                    calculateDisplayValue={(rowData: POInvoiceProduct) =>
                        rowData.itemDescription?.replace(new RegExp('\\r\\n', 'g'), '<br/>')
                    }
                    allowEditing={false}
                    width={300}
                />
                <Column
                    dataField={'thickness'}
                    caption={'Thickness'}
                    dataType="string"
                    visible={!isLaminate && !isServiceProvider}
                    allowEditing={false}
                />
                <Column
                    dataField={'dimensions'}
                    caption={'Dimensions'}
                    dataType="string"
                    visible={!isLaminate && !isServiceProvider}
                    allowEditing={false}
                />
                <Column
                    dataField={'squareFootage'}
                    caption={'Square Footage'}
                    dataType="string"
                    visible={!isLaminate && !isServiceProvider}
                    allowEditing={false}
                />
                <Column
                    dataField={'quantity'}
                    caption={'Quantity'}
                    dataType="number"
                    allowEditing={false}
                />
                <Column
                    dataField={'unitPrice'}
                    caption={'Unit Price'}
                    dataType="number"
                    showEditorAlways={true}
                    editorOptions={{ showSpinButtons: true, min: 0, format: "$#0.00" }}
                />
                <Column
                    dataField={'freightCost'}
                    caption={'Freight Cost'}
                    dataType="number"
                    allowEditing={false}
                    visible={!isServiceProvider}
                >
                    <Format type="currency" precision={2} ></Format>
                </Column>
                <Column
                    dataField={'totalCost'}
                    caption={'Total Cost'}
                    dataType="number"
                    allowEditing={false}
                    calculateCellValue={(row: POInvoiceProduct) => { return row.unitPrice * (row.quantity ?? 0) }}
                >
                    <Format type="currency" precision={2} ></Format>
                </Column>
            </DataGrid>
            <div className='dx-clearfix'>
                <table className='summary-table'>
                    <tbody>
                        <tr>
                            <td>Subtotal:</td>
                            <td>{formatNumber(invoice.details.subTotal, { type: 'currency', precision: 2 })}</td>
                        </tr>
                        <tr>
                            <td>Tax:</td>
                            <td>
                                <NumberBox
                                    value={invoice.details.tax}
                                    onValueChanged={(e) => {
                                        modify({
                                            ...invoice.details,
                                            tax: e.value,
                                            invoiceTotal: getTotal(invoice.details.subTotal, e.value,
                                                invoice.details.discount, invoice.details.cutCharge,
                                                invoice.details.freightAmount)
                                        });
                                    }}
                                    format="$#0.00"
                                    width={100}
                                    className="text-right"
                                >
                                </NumberBox>
                            </td>
                        </tr>
                        <tr>
                            <td>Discount:</td>
                            <td>
                                <NumberBox
                                    value={invoice.details.discount}
                                    onValueChanged={(e) => {
                                        modify({
                                            ...invoice.details,
                                            discount: e.value,
                                            invoiceTotal: getTotal(invoice.details.subTotal, invoice.details.tax,
                                                e.value, invoice.details.cutCharge,
                                                invoice.details.freightAmount)
                                        });
                                    }}
                                    format="$#0.00"
                                    width={100}
                                    className="text-right"
                                >
                                </NumberBox>
                            </td>
                        </tr>
                        {
                            !isServiceProvider &&
                            <tr>
                                <td>Cut Charge:</td>
                                <td>
                                    <NumberBox
                                        value={invoice.details.cutCharge}
                                        onValueChanged={(e) => {
                                            modify({
                                                ...invoice.details,
                                                cutCharge: e.value,
                                                invoiceTotal: getTotal(invoice.details.subTotal, invoice.details.tax,
                                                    invoice.details.discount, e.value,
                                                    invoice.details.freightAmount)
                                            });
                                        }}
                                        format="$#0.00"
                                        width={100}
                                        className="text-right"
                                    >
                                    </NumberBox>
                                </td>
                            </tr>
                        }
                        {
                            !isServiceProvider &&
                            <tr>
                                <td>Freight:</td>
                                <td>
                                    <NumberBox
                                        value={invoice.details.freightAmount}
                                        onValueChanged={(e) => {
                                            const subTotal = getSubtotal(products);
                                            modify({
                                                ...invoice.details,
                                                subTotal: subTotal,
                                                freightAmount: e.value,
                                                invoiceTotal: getTotal(subTotal, invoice.details.tax,
                                                    invoice.details.discount, invoice.details.cutCharge,
                                                    e.value),
                                                products: products
                                            });
                                        }}
                                        format="$#0.00"
                                        width={100}
                                        className="text-right"
                                    >
                                    </NumberBox>
                                </td>
                            </tr>
                        }
                        <tr className='total'>
                            <td>Total:</td>
                            <td>{formatNumber(invoice.details.invoiceTotal, { type: 'currency', precision: 2 })}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </>
    );
}
